import { cookieUtils } from "../utils/cookieUtils";
import axiosInstance from "./api.js";
import { logToServer } from "../utils/logs.js";
import { clearTokenRefreshTimer } from "../utils/tokenRefresh";
import { syncAdminFromMeUser, clearAdminCache } from "../utils/userRoles";
import {
  getApiErrorCode,
  getApiErrorExtras,
  getApiErrorMessage,
} from "../utils/apiErrorMessage";
import axios from "axios";
import { CONFIG } from "../config.js";
import { attachTransportErrorInterceptor } from "../errors/apiErrorBus";

const INVITATION_TOKEN_STORAGE_KEY = "pendingInvitationToken";
const LAMBDA_URL = CONFIG.LAMBDA_URL;

/** Cognito auth Lambda Function URL. CORS uses Allow-Origin: *; no cookies. */
const authLambdaClient = axios.create({
  baseURL: LAMBDA_URL,
  withCredentials: false,
});
attachTransportErrorInterceptor(authLambdaClient, "auth-lambda");

/**
 * Auth Lambda failures — carries machine `code` + resume extras so the landing
 * signup funnel can branch without parsing message strings.
 */
export class AuthServiceError extends Error {
  constructor(message, { status, actions, code, extras } = {}) {
    super(message);
    this.name = "AuthServiceError";
    this.status = status;
    this.actions = Array.isArray(actions) ? actions : [];
    this.code = typeof code === "string" ? code : "";
    this.extras =
      extras && typeof extras === "object" && !Array.isArray(extras)
        ? extras
        : {};
  }
}

/** Build AuthServiceError from an axios failure (or body-reported failure). */
const toAuthServiceError = (error, fallback = "Request failed") => {
  if (error instanceof AuthServiceError) {
    return error;
  }

  const data = error.response?.data || {};
  const firstErr = Array.isArray(data.errors) ? data.errors[0] : null;
  return new AuthServiceError(getApiErrorMessage(error, fallback), {
    status: data.statusCode ?? error.response?.status,
    actions: data.actions,
    code: getApiErrorCode(error) || firstErr?.code || "",
    extras: {
      ...getApiErrorExtras(error),
      field: firstErr?.field || data.field,
      errors: data.errors,
      next_step: data.next_step,
    },
  });
};

/** Throw when the API body reports failure even if HTTP status is 2xx. */
const assertAuthSuccess = (data, httpStatus, fallback = "Request failed") => {
  const status = data?.statusCode ?? httpStatus;
  const failed =
    data?.success === false || (typeof status === "number" && status >= 400);

  if (failed) {
    const fakeError = { response: { data, status } };
    throw new AuthServiceError(
      data?.message || data?.detail || fallback,
      {
        status,
        actions: data?.actions,
        code: getApiErrorCode(fakeError),
        extras: getApiErrorExtras(fakeError),
      },
    );
  }
};

const saveUserForUi = (payload) => {
  logToServer("INFO", `User data is ${payload.data} ${payload.user}`, payload);
  const user = payload.user || payload.data?.user || payload.data || payload;
  if (user?.email || user?.id || user?.userId || user?.userid) {
    cookieUtils.saveAuthData(user);
    return user;
  }
  return null;
};

export const AUTH_CHANGED_EVENT = "nova_auth_changed";

export const saveCognitoSessionToCookies = async () => {
  const response = await axiosInstance.get("/auth/me");
  const userData = response.data?.user || response.data;

  logToServer("INFO", "Response from /auth/me", {
    tenant_name: userData?.tenant_name,
    user_id: userData?.id,
    is_admin: userData?.is_admin,
  });

  if (userData) {
    syncAdminFromMeUser(userData);
    cookieUtils.saveAuthData(userData);
  }

  return userData;
};

/**
 * Re-fetch /auth/me, refresh UI auth cache, and notify listeners (sidebar, App gates).
 * Call after mutations that may change the current user's roles or menus.
 */
export const refreshAuthSession = async () => {
  const userData = await saveCognitoSessionToCookies();
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(AUTH_CHANGED_EVENT, { detail: { user: userData } }),
    );
  }
  return userData;
};

/**
 * Normalize signup snapshot responses (create / update / resume-draft / resume-payment / confirm).
 */
const normalizeSignupSnapshot = (data = {}) => ({
  success: true,
  message: data.message || "",
  signupId: data.signup_id || "",
  signupSessionToken: data.signup_session_token || "",
  signupPaymentToken: data.signup_payment_token || "",
  status: data.status || "",
  email: data.email || "",
  firstName: data.firstname || "",
  lastName: data.lastname || "",
  tenantName: data.tenant_name || "",
  tenantSlug: data.tenant_slug || "",
  tenantHost: data.tenant_host || "",
  subdomain: data.subdomain || data.tenant_slug || "",
  // ISO country codes must stay uppercase so landing <select> options match.
  region: String(data.region || "").trim().toUpperCase(),
  phoneNumber: data.phonenumber ?? "",
  gstin: data.gstin || "",
  otpSentAt: data.otp_sent_at || "",
  nextStep: data.next_step || "",
  locked: Boolean(data.locked),
});

/**
 * POST /signup/check-subdomain — availability before submit (blur / debounce).
 * Optional signup_id excludes the current draft when editing.
 */
export const checkSignupSubdomain = async ({ subdomain, signupId } = {}) => {
  try {
    const payload = {
      subdomain: subdomain ? subdomain.trim().toLowerCase() : "",
    };
    if (signupId) {
      payload.signup_id = signupId;
    }

    const response = await authLambdaClient.post(
      "/signup/check-subdomain",
      payload,
    );
    const data = response.data || {};
    assertAuthSuccess(data, response.status, "Unable to check workspace URL");

    return {
      success: true,
      available: data.available !== false,
      message: data.message || "",
      code: data.code || "",
      tenantSlug: data.tenant_slug || "",
      tenantHost: data.tenant_host || "",
    };
  } catch (error) {
    throw toAuthServiceError(error, "Unable to check workspace URL");
  }
};

/**
 * POST /signup — create (omit ids) or in-place update before confirm
 * (include signup_id + signup_session_token + password).
 */
export const registerUser = async ({
  firstName,
  lastName,
  email,
  phoneNumber,
  tenantName,
  subdomain,
  region,
  password,
  marketplaceSession,
  signupId,
  signupSessionToken,
  gstin,
}) => {
  try {
    const payload = {
      email,
      password,
      firstname: firstName,
      lastname: lastName,
      tenant_name: tenantName,
      phonenumber: phoneNumber ? String(phoneNumber).trim() : "",
      subdomain: subdomain ? subdomain.trim().toLowerCase() : "",
      region: region || "",
    };
    if (signupId) {
      payload.signup_id = signupId;
    }
    if (signupSessionToken) {
      payload.signup_session_token = signupSessionToken;
    }
    if (marketplaceSession) {
      payload.marketplace_session = marketplaceSession;
    }
    if (gstin) {
      payload.gstin = gstin;
    }

    const response = await authLambdaClient.post("/signup", payload);
    const data = response.data || {};
    assertAuthSuccess(data, response.status, "Signup failed");
    return normalizeSignupSnapshot(data);
  } catch (error) {
    throw toAuthServiceError(error, "Signup failed");
  }
};

/**
 * POST /signup/resume-draft — recover pre-confirm session with email + password
 * when signup_session_token is missing/expired.
 */
export const resumeSignupDraft = async ({ email, password }) => {
  try {
    const response = await authLambdaClient.post("/signup/resume-draft", {
      email,
      password,
    });
    const data = response.data || {};
    assertAuthSuccess(data, response.status, "Unable to resume signup");
    return normalizeSignupSnapshot(data);
  } catch (error) {
    throw toAuthServiceError(error, "Unable to resume signup");
  }
};

/**
 * POST /signup/resume-payment — mint a new signup_payment_token after email
 * is confirmed (token expired / returning next day).
 */
export const resumeSignupPayment = async ({ email, password }) => {
  try {
    const response = await authLambdaClient.post("/signup/resume-payment", {
      email,
      password,
    });
    const data = response.data || {};
    assertAuthSuccess(data, response.status, "Unable to resume payment");
    return normalizeSignupSnapshot(data);
  } catch (error) {
    throw toAuthServiceError(error, "Unable to resume payment");
  }
};

/**
 * POST /confirm-email — prefer signup_id; omit expired marketplace_session.
 */
export const confirmEmail = async ({
  email,
  confirmationCode,
  signupId,
  marketplaceSession,
}) => {
  try {
    const payload = {
      email,
      confirmation_code: confirmationCode,
    };
    if (signupId) {
      payload.signup_id = signupId;
    }
    if (marketplaceSession) {
      payload.marketplace_session = marketplaceSession;
    }

    const response = await authLambdaClient.post("/confirm-email", payload);
    const data = response.data || {};
    assertAuthSuccess(data, response.status, "Confirmation failed");
    return normalizeSignupSnapshot(data);
  } catch (error) {
    throw toAuthServiceError(error, "Confirmation failed");
  }
};

export const resendConfirmationCode = async ({ email }) => {
  try {
    const response = await authLambdaClient.post("/resend-confirmation", {
      email,
    });

    const data = response.data || {};
    assertAuthSuccess(data, response.status, "Failed to resend code");

    return {
      success: true,
      message: data.message,
      nextStep: data.next_step || "otp",
    };
  } catch (error) {
    throw toAuthServiceError(error, "Failed to resend code");
  }
};

/** POST /forgot-password on the auth Lambda; Cognito emails a reset OTP. */
export const forgotPassword = async ({ email }) => {
  try {
    const response = await authLambdaClient.post("/forgot-password", {
      email,
    });

    const data = response.data || {};
    assertAuthSuccess(data, response.status, "Failed to send password reset code");

    return {
      success: true,
      message: data.message,
    };
  } catch (error) {
    throw toAuthServiceError(error, "Failed to send password reset code");
  }
};

/** POST /reset-password on the auth Lambda; confirm OTP and set the new password. */
export const resetPassword = async ({
  email,
  confirmationCode,
  newPassword,
}) => {
  try {
    const response = await authLambdaClient.post("/reset-password", {
      email,
      confirmation_code: confirmationCode,
      new_password: newPassword,
    });

    const data = response.data || {};
    assertAuthSuccess(data, response.status, "Failed to reset password");

    return {
      success: true,
      message: data.message,
    };
  } catch (error) {
    throw toAuthServiceError(error, "Failed to reset password");
  }
};

export const loginUser = async ({ email, password }) => {
  try {
    const response = await authLambdaClient.post("/login", {
      email,
      password,
    });
    const data = response.data || {};
    assertAuthSuccess(data, response.status, "Login failed");
    logToServer("INFO", `Response data from login page: `, data);
    return response;
  } catch (error) {
    throw toAuthServiceError(error, "Login failed");
  }
};

export const logoutUser = async () => {
  try {
    const res = await axiosInstance.post("/auth/logout");

    if (res.data.success) {
      console.log(res.data.message);
    }

    return res.data;
  } finally {
    clearTokenRefreshTimer();
    clearAdminCache();
    cookieUtils.clearAuthCookies();
  }
};
export const getStoredInvitationToken = () =>
  window.sessionStorage.getItem(INVITATION_TOKEN_STORAGE_KEY);

export const clearStoredInvitationToken = () => {
  window.sessionStorage.removeItem(INVITATION_TOKEN_STORAGE_KEY);
};

export const getAccessToken = async () => null;

export const getUser = async () => {
  const response = await axiosInstance.get("/auth/me");
  const userData = response.data?.user || response.data;
  if (userData) {
    syncAdminFromMeUser(userData);
  }
  return response.data;
};

export const loginWithGoogle = ({ invitationToken } = {}) => {
  if (invitationToken) {
    sessionStorage.setItem(INVITATION_TOKEN_STORAGE_KEY, invitationToken);
  }
  window.location.href = `${CONFIG.REACT_APP_BACKEND_URL}/auth/google/login`;
};
